/**
 * How a programme is written for a reader, and the option lists built from it.
 *
 * Values come from the canonical PROGRAMME_OPTIONS in @padcanvas/shared; only
 * the display wording lives here ('STATE' → 'State Board'). Kept out of the
 * registration barrel so a filter control can reuse it without importing that
 * barrel's pages.
 *
 * Two lists, because visibility is a property of the surface: student- and
 * teacher-facing pickers offer the BASE programmes, and only an authoring
 * surface offers the full vocabulary.
 */
import { PROGRAMME_OPTIONS, INTERNATIONAL_PROGRAMME_OPTIONS, ALL_PROGRAMME_OPTIONS, PROGRAMME_GRADES, programmeStage, type AllProgrammeType } from '@padcanvas/shared';

export const PROGRAMME_LABELS: Record<string, string> = {
  JEE: 'JEE',
  NEET: 'NEET',
  CBSE: 'CBSE',
  ICSE: 'ICSE',
  STATE: 'State Board',
  IB: 'IB',
  CAMBRIDGE: 'Cambridge',
  AP: 'AP (College Board)',
  COMMON_CORE: 'Common Core',
  US_STATE_DISTRICT: 'US State / District',
};

/** `{ value, label }` for the BASE programmes, in canonical order — what every
 *  student- and teacher-facing picker offers. */
export const PROGRAMME_FORM_OPTIONS = PROGRAMME_OPTIONS.map(v => ({
  value: v as string,
  label: PROGRAMME_LABELS[v] ?? v,
}));

/** `{ value, label }` for the base programmes plus, when `international` is
 *  true, IB and Cambridge. Student and teacher pickers pass the
 *  `international_programmes` gate; admin surfaces pass `true` — they are
 *  never gated. */
export function programmeFormOptions({ international }: { international: boolean }): { value: string; label: string }[] {
  const values: readonly string[] = international
    ? [...PROGRAMME_OPTIONS, ...INTERNATIONAL_PROGRAMME_OPTIONS]
    : PROGRAMME_OPTIONS;
  return values.map(v => ({ value: v, label: PROGRAMME_LABELS[v] ?? v }));
}

/** True for one of the base programmes. A student picker lists these up front
 *  and keeps the rest (IB, Cambridge) behind "Show all", so the common choices are
 *  not buried in a row of programmes most students never pick. */
export const isBaseProgramme = (value: string): boolean =>
  (PROGRAMME_OPTIONS as readonly string[]).includes(value);

/** `{ value, label }` for the FULL vocabulary — authoring surfaces (admin
 *  curricula) where an extended programme must be selectable. */
export const ALL_PROGRAMME_FORM_OPTIONS = ALL_PROGRAMME_OPTIONS.map(v => ({
  value: v as string,
  label: PROGRAMME_LABELS[v] ?? v,
}));

/** The reader-facing name for one programme; the value itself if unknown. */
export const programmeLabel = (value: string): string => PROGRAMME_LABELS[value] ?? value;

/** 'Classes 8–10' for a programme that covers only some grades; null for one
 *  that covers them all. Shown beside a programme a picker has to disable. */
export const programmeGradeRangeLabel = (value: string): string | null => {
  const grades = PROGRAMME_GRADES[value as AllProgrammeType];
  return grades ? `Classes ${grades[0]}–${grades[grades.length - 1]}` : null;
};

/** 'Cambridge (AS Level)' — the programme with the qualification it is at in
 *  this grade; the bare label for a programme without stages. */
export const programmeLabelAtGrade = (value: string, grade: string | number | null | undefined): string => {
  const stage = programmeStage(value, grade);
  return stage ? `${programmeLabel(value)} (${stage})` : programmeLabel(value);
};
